import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { missingAnimalService, MissingAnimal } from '../services/missingAnimalService';
import { uploadService } from '../services/uploadService';
import './News.css';
import './MissingAnimals.css';

const News: React.FC = () => {
  const { user } = useAuth();
  const [missingAnimals, setMissingAnimals] = useState<MissingAnimal[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFound, setShowFound] = useState(false);
  const [selectedAnimal, setSelectedAnimal] = useState<MissingAnimal | null>(null);
  const [markingAsFound, setMarkingAsFound] = useState(false);

  useEffect(() => {
    loadMissingAnimals();
  }, [showFound]);

  const loadMissingAnimals = async () => {
    try {
      setLoading(true);
      const data = await missingAnimalService.getMissingAnimals(showFound);
      setMissingAnimals(data);
    } catch (error: any) {
      console.error('Erro ao carregar animais desaparecidos:', error);
      alert(error.response?.data?.error || 'Erro ao carregar animais desaparecidos');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsFound = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja marcar este animal como encontrado?')) {
      return;
    }

    try {
      setMarkingAsFound(true);
      await missingAnimalService.markAsFound(id);
      alert('Animal marcado como encontrado com sucesso!');
      loadMissingAnimals();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erro ao marcar animal como encontrado');
    } finally {
      setMarkingAsFound(false);
    }
  };

  const activeMissing = missingAnimals.filter(ma => !ma.is_found);
  const foundMissing = missingAnimals.filter(ma => ma.is_found);
  const listToShow = showFound ? foundMissing : activeMissing;

  return (
    <div className="container">
      <div className="page-header">
        <h1>Notícias</h1>
        <Link to="/missing-animals/report" className="btn btn-primary">
          Reportar Animal Desaparecido
        </Link>
      </div>

      <div className="card">
        <h2>Bem-vindo à Central de Notícias</h2>
        <p>
          Esta é a seção de notícias do sistema Causa Animal. Aqui você encontra os animais
          desaparecidos e pode reportar novos desaparecimentos.
        </p>
      </div>

      <div className="card">
        <h2>Animais Desaparecidos</h2>
        <p>
          Se você perdeu seu animal ou encontrou um animal perdido, reporte ou confira os casos ativos abaixo.
        </p>
        <div style={{ display: 'flex', gap: '10px', marginTop: '16px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowFound(!showFound)}
            className={`btn ${showFound ? 'btn-secondary' : 'btn-primary'}`}
          >
            {showFound ? 'Mostrar Apenas Ativos' : 'Mostrar Encontrados'}
          </button>
        </div>

        {loading ? (
          <p style={{ marginTop: '20px', color: '#666' }}>Carregando...</p>
        ) : !showFound && activeMissing.length === 0 ? (
          <p style={{ marginTop: '20px', color: '#666' }}>Nenhum animal desaparecido no momento.</p>
        ) : showFound && foundMissing.length === 0 ? (
          <p style={{ marginTop: '20px', color: '#666' }}>Nenhum animal encontrado registrado.</p>
        ) : (
          <div className="missing-animals-grid">
            {listToShow.map((missingAnimal) => (
              <div key={missingAnimal.id} className={`missing-animal-card ${missingAnimal.is_found ? 'found' : ''}`}>
                {missingAnimal.image_url && (
                  <div className="missing-animal-image">
                    <img
                      src={uploadService.getImageUrl(missingAnimal.image_url)}
                      alt={missingAnimal.animal?.name || 'Animal'}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                )}
                <div className="missing-animal-content">
                  <h3>{missingAnimal.animal?.name || 'Animal sem nome'}</h3>
                  <div className="missing-animal-info">
                    <p><strong>Espécie:</strong> {missingAnimal.animal?.species || '-'}</p>
                    {missingAnimal.animal?.breed && (
                      <p><strong>Raça:</strong> {missingAnimal.animal.breed}</p>
                    )}
                    {missingAnimal.animal?.color && (
                      <p><strong>Cor:</strong> {missingAnimal.animal.color}</p>
                    )}
                    <p><strong>Última localização:</strong> {missingAnimal.last_seen_location}</p>
                    {missingAnimal.description && (
                      <p><strong>Descrição:</strong> {missingAnimal.description}</p>
                    )}
                    {missingAnimal.contact_email && (
                      <p><strong>Email:</strong> {missingAnimal.contact_email}</p>
                    )}
                    {missingAnimal.contact_phone && (
                      <p><strong>Telefone:</strong> {missingAnimal.contact_phone}</p>
                    )}
                    <p className="missing-date">
                      <strong>Desaparecido em:</strong>{' '}
                      {new Date(missingAnimal.created_at).toLocaleDateString('pt-BR')}
                    </p>
                    {missingAnimal.is_found && missingAnimal.found_at && (
                      <p className="found-date">
                        <strong>Encontrado em:</strong>{' '}
                        {new Date(missingAnimal.found_at).toLocaleDateString('pt-BR')}
                      </p>
                    )}
                  </div>
                  <div className="missing-animal-actions">
                    <button
                      onClick={() => setSelectedAnimal(missingAnimal)}
                      className="btn btn-secondary"
                      style={{ marginRight: '8px', marginBottom: '8px' }}
                    >
                      Ver detalhes
                    </button>
                    {!missingAnimal.is_found && missingAnimal.animal && user?.id === missingAnimal.animal?.owner_id && (
                      <button
                        onClick={() => handleMarkAsFound(missingAnimal.id)}
                        className="btn btn-success"
                        disabled={markingAsFound}
                      >
                        Marcar como Encontrado
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card">
        <h2>Como Funciona</h2>
        <ul className="news-list">
          <li>
            <strong>Reportar Desaparecimento:</strong> Se seu animal desapareceu, você pode criar
            um alerta informando onde ele foi visto pela última vez e como entrar em contato.
          </li>
          <li>
            <strong>Adicionar Foto:</strong> Você pode anexar uma foto do seu animal para facilitar
            a identificação. Se não tiver foto, pode fornecer uma descrição detalhada.
          </li>
          <li>
            <strong>Marcar como Encontrado:</strong> Quando seu animal for encontrado, você pode
            marcar o alerta como resolvido.
          </li>
          <li>
            <strong>Ajudar na Busca:</strong> Todos podem visualizar os animais desaparecidos e
            ajudar na busca compartilhando as informações.
          </li>
        </ul>
      </div>

      {selectedAnimal && (
        <div className="modal-overlay" onClick={() => setSelectedAnimal(null)}>
          <div className="modal-content modal-detail" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Detalhes do Animal Desaparecido</h2>
              <button
                className="modal-close"
                onClick={() => setSelectedAnimal(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {selectedAnimal.image_url && (
                <div className="detail-image">
                  <img
                    src={uploadService.getImageUrl(selectedAnimal.image_url)}
                    alt={selectedAnimal.animal?.name || 'Animal'}
                  />
                </div>
              )}
              <div className="detail-section">
                <h3>Informações do Animal</h3>
                <dl className="detail-list">
                  <dt>Nome</dt>
                  <dd>{selectedAnimal.animal?.name || '-'}</dd>
                  <dt>Espécie</dt>
                  <dd>{selectedAnimal.animal?.species || '-'}</dd>
                  <dt>Raça</dt>
                  <dd>{selectedAnimal.animal?.breed || '-'}</dd>
                  <dt>Cor</dt>
                  <dd>{selectedAnimal.animal?.color || '-'}</dd>
                  {selectedAnimal.description && (
                    <>
                      <dt>Descrição Adicional</dt>
                      <dd>{selectedAnimal.description}</dd>
                    </>
                  )}
                </dl>
              </div>
              <div className="detail-section">
                <h3>Informações de Desaparecimento</h3>
                <dl className="detail-list">
                  <dt>Última Localização</dt>
                  <dd>{selectedAnimal.last_seen_location}</dd>
                  <dt>Data do Desaparecimento</dt>
                  <dd>{new Date(selectedAnimal.created_at).toLocaleDateString('pt-BR')}</dd>
                  {selectedAnimal.contact_email && (
                    <>
                      <dt>Email para Contato</dt>
                      <dd>{selectedAnimal.contact_email}</dd>
                    </>
                  )}
                  {selectedAnimal.contact_phone && (
                    <>
                      <dt>Telefone para Contato</dt>
                      <dd>{selectedAnimal.contact_phone}</dd>
                    </>
                  )}
                </dl>
              </div>
              <div className="modal-actions">
                <button
                  onClick={() => setSelectedAnimal(null)}
                  className="btn btn-secondary"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default News;
